import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTouchDevice } from '@features/onboarding/hooks/useTouchDevice';
import './DropdownFloating.scss';
import { DropdownFloatingSearch } from './search';

import { cn } from '@utils/index';

// Option type for dropdown
type Option = string | { label: string; value: string };

// Props for DropdownFloating
interface DropdownFloatingProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  id: string;
  label: string;
  options: Option[];
  placeholder?: string;
  wrapperClass?: string;
  inputClass?: string;
  labelClass?: string;
  chevronClass?: string;
  onChange: (value: string) => void;
  error?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

const DropdownFloating = React.forwardRef<
  HTMLSelectElement | HTMLDivElement,
  DropdownFloatingProps
>(
  (
    {
      id,
      label,
      options,
      placeholder = '',
      value,
      wrapperClass = '',
      inputClass = '',
      labelClass = '',
      chevronClass = '',
      onChange,
      error,
      disabled,
      allowEmpty = true,
      emptyLabel = 'Not selected',
      ...rest
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isTouchDevice = useTouchDevice();

    useEffect(() => {
      const closeOnOutsideClick = (e: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('click', closeOnOutsideClick);
      return () => document.removeEventListener('click', closeOnOutsideClick);
    }, []);

    const normalizedOptions = options.map((opt) =>
      typeof opt === 'string' ? { label: opt, value: opt } : opt
    );

    // Добавляем опцию "Не выбрано" если allowEmpty = true
    const optionsWithEmpty = allowEmpty 
      ? [{ label: emptyLabel, value: '' }, ...normalizedOptions]
      : normalizedOptions;

    // На desktop показываем label только из реальных опций (без пустого варианта)
    const selectedLabel = normalizedOptions.find((opt) => opt.value === value)?.label;

    const baseWrapperClass = `dropdown-floating ${wrapperClass}`;
    const baseInputClass = `dropdown-floating__field ${inputClass} ${error ? 'dropdown-floating__field_error' : ''}`;
    const baseLabelClass = `dropdown-floating__label ${labelClass}`;

    // Если allowEmpty=true и выбрано пустое значение, то считаем что выбор сделан
    const hasValueCheck = allowEmpty 
      ? (value !== undefined && value !== null)
      : Boolean(value && value.toString().trim().length > 0);

    const [hasValue, setHasValue] = useState(hasValueCheck);

    useEffect(() => {
      const newHasValue = allowEmpty 
        ? (value !== undefined && value !== null)
        : Boolean(value && value.toString().trim().length > 0);
      setHasValue(newHasValue);
    }, [value, allowEmpty]);

    if (isTouchDevice) {
      return (
        <div
          className={`${baseWrapperClass} ${hasValue ? 'dropdown-floating_has-value' : ''} ${
            disabled ? 'dropdown-floating_disabled' : ''
          }`}
        >
          <select
            id={id}
            ref={ref as React.Ref<HTMLSelectElement>}
            className={`${baseInputClass} dropdown-floating__field_select ${value ? '': 'text-white'}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            {...rest}
          >
            {placeholder && !allowEmpty && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {optionsWithEmpty.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label htmlFor={id} className={baseLabelClass}>
            {label}
          </label>
          <ChevronDown className={cn('dropdown-floating__chevron', chevronClass)} />
        </div>
      );
    }

    return (
      <div
        className={`${baseWrapperClass} ${hasValue ? 'dropdown-floating_has-value' : ''} ${
          disabled ? 'dropdown-floating_disabled' : ''
        }`}
        ref={dropdownRef}
        tabIndex={-1}
        onBlur={(e) => {
          if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
            setOpen(false);
          }
        }}
      >
        <div
          className={`${baseInputClass} dropdown-floating__field_trigger`}
          onClick={() => !disabled && setOpen((prev) => !prev)}
        >
          <span className="dropdown-floating__field-text !font-secondary">
            {selectedLabel || placeholder}
          </span>
          <ChevronDown
            className={cn(
              'dropdown-floating__chevron',
              chevronClass,
              open && 'dropdown-floating__chevron_open'
            )}
          />
        </div>
        <label htmlFor={id} className={baseLabelClass}>
          {label}
        </label>
        <div
          className={`dropdown-floating__menu ${open ? 'dropdown-floating__menu_open' : ''}`}
        >
          <ul className="dropdown-floating__menu-list">
            {normalizedOptions.map((opt) => (
              <li
                key={opt.value}
                className="dropdown-floating__menu-item !font-secondary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
);

DropdownFloating.displayName = 'DropdownFloating';

export { DropdownFloating, DropdownFloatingSearch };